import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/Layout/PageLayout';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import ExportDataDropDown from '@/components/molecules/ExportDataDropDown';
import DownloadOptions from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import { useCageName } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { useIsSmolt } from '@/hooks/useCageTypeFlags';
import { toDateString } from '@/utils/date';

import CageWeightDistributionView from './CageWeightDistributionView';
import {
    useWeightDistributionDispatch,
    useWeightDistributionState,
} from './cage-weight-distribution-context';
import customNames from './custom-names';
import { buildWeightDistributionExport } from './weightDistributionExport';

const AGGREGATED_DISTRIBUTION_TYPE = 'agg';
const AVERAGE_DISTRIBUTION_TYPE = 'avg';
const DEFAULT_FISH_NAME = 'Finn';

export default function CageWeightDistributionContainer() {
    const { t } = useTranslation();
    const cageName = useCageName();
    const { to, from, setFilter } = useCageFilter();
    const isSmolt = useIsSmolt();

    const {
        isLoading,
        error,
        weightDistributionData,
        weightDistributionHistogram,
        distributionStats,
        distributionType,
        currentIndex,
        day,
        weightUnitTag,
    } = useWeightDistributionState();
    const { setDistributionType, setCurrentIndex, retry } = useWeightDistributionDispatch();

    const exportData = useMemo(
        () => buildWeightDistributionExport(weightDistributionData),
        [weightDistributionData]
    );

    const onDateChange = useCallback(
        (dates: DateRange) => {
            setFilter?.({ from: dates.dateFrom, to: dates.dateTo });
        },
        [setFilter]
    );

    const onAggregatedChange = useCallback(
        (isAggregated: boolean) => {
            setDistributionType(
                isAggregated ? AGGREGATED_DISTRIBUTION_TYPE : AVERAGE_DISTRIBUTION_TYPE
            );
        },
        [setDistributionType]
    );

    // Picked once per mount. Deriving it during render re-rolled the name on
    // every render, so the fish was renamed whenever anything on the page moved.
    const fishName = useMemo(
        () => customNames[Math.floor(Math.random() * customNames.length)] ?? DEFAULT_FISH_NAME,
        []
    );

    const lastDay = weightDistributionData.at(-1)?.day;

    return (
        <PageLayout
            title={cageName}
            subtitle={t('cageWeight.distribution.weightDistribution')}
            actions={
                <ExportDataDropDown disabled={isLoading}>
                    <DownloadOptions data={exportData.rows} columns={exportData.columns} />
                </ExportDataDropDown>
            }>
            <CageWeightDistributionView
                histogram={weightDistributionHistogram}
                stats={distributionStats}
                day={day}
                currentIndex={currentIndex}
                dayCount={weightDistributionData.length}
                onIndexChange={setCurrentIndex}
                weightUnitTag={weightUnitTag}
                isAggregated={distributionType === AGGREGATED_DISTRIBUTION_TYPE}
                onAggregatedChange={onAggregatedChange}
                isSmolt={isSmolt}
                showNamedFish={!!day && day === lastDay}
                fishName={fishName}
                isLoading={isLoading}
                error={error}
                retry={retry}
                dateFrom={toDateString(from)}
                dateTo={toDateString(to)}
                onDateChange={onDateChange}
            />
        </PageLayout>
    );
}
